import { useMemo } from "react";
import { useApi, apiResolver } from "@/api/useApi";
import {
  Simulator,
  UpsertSimulatorParamsDto,
  UpsertReleaseParamsDto,
  GetSimulatorReleasesWithConversationsResponse,
  GetPostAnswerActionsOptions,
} from "./Simulator.model";
import { useQuery } from "react-query";

export function useSimulatorApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      getSimulators: async () =>
        apiResolver<Simulator[]>(api.get("/platform/simulators")),
      getSimulator: async (
        simulatorId: string,
        includeInteractionEvents?: boolean
      ) => {
        const params = new URLSearchParams();
        if (includeInteractionEvents)
          params.append("includeInteractionEvents", "true");

        return apiResolver<Simulator>(
          api.get(
            `/platform/simulators/${simulatorId}${
              params.toString() ? `?${params.toString()}` : ""
            }`
          )
        );
      },
      createSimulator: async (input: UpsertSimulatorParamsDto) =>
        apiResolver<Simulator>(api.post("/platform/simulators", input)),
      updateSimulator: async (
        simulatorId: string,
        input: UpsertSimulatorParamsDto
      ) =>
        apiResolver<Simulator>(
          api.put(`/platform/simulators/${simulatorId}`, input)
        ),
      deleteSimulator: async (simulatorId: string) =>
        apiResolver<{ success: boolean }>(
          api.delete(`/platform/simulators/${simulatorId}`)
        ),
      getSimulatorReleases: async (
        simulatorId: string,
        includeConversations?: boolean,
        includeMessages?: boolean,
        includeTopicReleasePolicies?: boolean
      ) => {
        const params = new URLSearchParams();
        if (includeConversations) params.append("includeConversations", "true");
        if (includeMessages) params.append("includeMessages", "true");
        if (includeTopicReleasePolicies)
          params.append("includeTopicReleasePolicies", "true");

        return apiResolver<GetSimulatorReleasesWithConversationsResponse>(
          api.get(
            `/platform/simulators/${simulatorId}/releases${
              params.toString() ? `?${params.toString()}` : ""
            }`
          )
        );
      },
      publishRelease: async (
        simulatorId: string,
        releaseId: string,
        input: UpsertReleaseParamsDto
      ) =>
        apiResolver<Simulator>(
          api.post(
            `/platform/simulators/${simulatorId}/releases/${releaseId}`,
            input
          )
        ),
      getSimulatorPostAnswerActionsOptions: async (simulatorId: string) =>
        apiResolver<GetPostAnswerActionsOptions>(
          api.get(`/platform/simulators/${simulatorId}/post-answer-actions/options`)
        ),
    }),
    [api]
  );
}

export function useGetSimulator(simulatorId?: string) {
  const { getSimulator } = useSimulatorApi();

  return useQuery({
    queryKey: ["simulator", simulatorId],
    queryFn: () => getSimulator(simulatorId ?? ""),
    enabled: simulatorId != null,
  });
}

export function useGetSimulatorPostAnswerActionsOptions(simulatorId?: string) {
  const { getSimulatorPostAnswerActionsOptions } = useSimulatorApi();

  return useQuery({
    queryKey: ["simulatorPostAnswerActionsOptions", simulatorId],
    queryFn: () => getSimulatorPostAnswerActionsOptions(simulatorId ?? ""),
    enabled: simulatorId != null,
  });
}
